import {
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import { IconButton } from '@gamma/buttons';
import ArrowLeftOutlinedIcon from '@gamma/icons/ArrowLeftOutlinedIcon';
import ContentCopyOutlinedIcon from '@gamma/icons/ContentCopyOutlinedIcon';
import FullscreenOutlinedIcon from '@gamma/icons/FullscreenOutlinedIcon';
import { ReactNode, useEffect } from 'react';

export interface EditorActionsTabProps {
  value: string;
  isCopyable?: boolean;
  isFullScreen?: boolean;
  isFullScreenOnOpen?: () => void;
  toastOnCopy?: boolean;
  children?: ReactNode;
  'data-testid'?: string;
}

export const EditorActionsTab = ({
  value,
  isCopyable,
  isFullScreen,
  isFullScreenOnOpen,
  toastOnCopy,
  children,
  'data-testid': dataTestId = 'editor-actions-tab',
}: EditorActionsTabProps) => {
  const { hasCopied, onCopy } = useClipboard(value);
  const toast = useToast();

  useEffect(() => {
    if (toastOnCopy && hasCopied) {
      toast({
        position: 'top',
        status: 'success',
        description: `Copied to clipboard.`,
      });
    }
  }, [hasCopied, toast, toastOnCopy]);

  if (!children && !isCopyable) {
    return null;
  }

  return (
    <Flex
      top={4}
      right={-4}
      position="absolute"
      zIndex={1}
      gap={2}
      alignItems="stretch"
    >
      <Menu placement="left">
        <MenuButton
          as={IconButton}
          p={0}
          variant="solid"
          colorScheme="gray"
          aria-label="actions"
          minW="0px"
          minH={10}
          maxW={4}
          borderBottomLeftRadius="none"
          borderTopLeftRadius="none"
          data-testid={dataTestId}
          overflow="hidden"
        >
          <ArrowLeftOutlinedIcon mx={-1} />
        </MenuButton>
        <Portal>
          <MenuList
            py={0}
            minW="none"
            data-testid={`${dataTestId}-menu-list`}
            zIndex="popover"
          >
            {isFullScreen && (
              <MenuItem
                icon={<FullscreenOutlinedIcon />}
                onClick={isFullScreenOnOpen}
                data-testid={`${dataTestId}-menu-full-screen`}
              >
                Full screen
              </MenuItem>
            )}
            {isCopyable && (
              <MenuItem
                icon={<ContentCopyOutlinedIcon />}
                onClick={onCopy}
                data-testid={`${dataTestId}-menu-copy`}
              >
                Copy
              </MenuItem>
            )}
            {children}
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  );
};
